import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion as MOTION } from "framer-motion";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import resolveImageUrl from "../../utils/imageUtils";
import { Toaster, toast } from "react-hot-toast";
import { Calendar } from "lucide-react";

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const toFiniteNumber = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const n = Number(String(val).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  const n = toFiniteNumber(str);
  if (n === null) return str;
  return String(Math.round(n));
};

const hasUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim().toLowerCase();
  return str !== "" && str !== "null" && str !== "undefined";
};

const pickFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (hasUsableValue(value)) return value;
  }
  return "";
};

const NotResolved = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [circles, setCircles] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [showFullImage, setShowFullImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingComment, setMeetingComment] = useState("");
  const [requestingMeeting, setRequestingMeeting] = useState(false);
  const itemsPerPage = 12;

  const [searchPin, setSearchPin] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [searchedProperties, setSearchedProperties] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [highlightPin, setHighlightPin] = useState("");

  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;

  const getPropertyImage = (p) => {
    if (!p) return null;
    if (p.image) return p.image;
    if (p.image_url) return p.image_url;
    if (p.imageUrl) return p.imageUrl;
    if (p.image_path) return p.image_path;
    if (Array.isArray(p.images) && p.images.length > 0) return p.images[0];
    if (Array.isArray(p.property_images) && p.property_images.length > 0) {
      const first = p.property_images[0];
      return first?.image || first?.url || first?.path || null;
    }
    return null;
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false,
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSearchedProperties((prev) =>
        prev ? prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p)) : null
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", { id: toastId });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const fetchCircles = async () => {
    try {
      const res = await axios.get("/properties/circles");
      setCircles(res.data || []);
    } catch {
      console.error("Failed to load circles");
    }
  };

  const fetchProperties = async (page = 1) => {
    try {
      const currentUserId = userId;
      const res = await axios.get("/properties", {
        params: {
          status: "unresolved",
          page,
          limit: itemsPerPage,
          circle: selectedCircle || undefined,
        },
      });

      const list = (res.data?.data || []).filter((p) => p.supervisor_user_id === currentUserId);
      const toTime = (val) => {
        const t = val ? new Date(val).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      };
      const sorted = [...list].sort((a, b) => {
        const at = toTime(a.reassessment_date) || (Number(a.id) || 0);
        const bt = toTime(b.reassessment_date) || (Number(b.id) || 0);
        if (bt !== at) return bt - at;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      });

      // setProperties(sorted);
      const fetchBust = Date.now();
      setProperties(sorted.map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
      setCurrentPage(page);
      setSearchedProperties(null);
      setSearchMessage("");
    } catch (err) {
      console.error("Failed to fetch properties:", err);
    }
  };

  const handleSearchByPin = async () => {
    const pin = searchPin.trim();
    if (!pin) {
      setSearchMessage("Please enter a PIN.");
      setSearchedProperties(null);
      return;
    }

    try {
      const currentUserId = userId;
      setSearching(true);
      setSearchMessage("");
      setSearchedProperties(null);

      const res = await axios.get("/properties", {
        params: { status: "unresolved", limit: 10000, circle: selectedCircle || undefined },
      });

      const list = (res.data?.data || []).filter((p) => p.supervisor_user_id === currentUserId);
      const found = list.find(
        (p) => String(p.pin || "").trim().toLowerCase() === pin.toLowerCase()
      );

      if (!found) {
        setSearchMessage("No unresolved property found with this PIN.");
        setSearchedProperties([]);
        return;
      }

      setSearchedProperties([found]);
      setSearchMessage(`Found 1 property with PIN: ${pin}`);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setSearchMessage("Failed to search property by PIN.");
    } finally {
      setSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchPin("");
    setSearchMessage("");
    setSearchedProperties(null);
    setSelectedCircle("");
    fetchProperties(1);
  };

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
    setIsModalOpen(true);
    setRotation(0);
  };

  const closeModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setIsModalOpen(false);
    setSelectedProperty(null);
    setRotation(0);
    setTimeline([]);
    setTimelineError("");
    setTimelineLoading(false);
    highlightPropertyCard(pinToHighlight);
  };

  useEffect(() => {
    const pin = selectedProperty?.pin;
    if (!isModalOpen || !pin) {
      setTimeline([]);
      setTimelineError("");
      setTimelineLoading(false);
      return;
    }

    const fetchTimeline = async () => {
      try {
        setTimelineLoading(true);
        setTimelineError("");
        const params = selectedProperty?.id ? { propertyId: selectedProperty.id } : {};
        const res = await axios.get(`/properties/timeline-id/${selectedProperty.id}`, { params });
        setTimeline(res.data?.timeline || []);
      } catch (err) {
        console.error("Failed to fetch timeline:", err);
        setTimeline([]);
        setTimelineError("Failed to load previous comments.");
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimeline();
  }, [isModalOpen, selectedProperty?.pin, selectedProperty?.id]);

  useEffect(() => {
    fetchCircles();
    fetchProperties(1);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchProperties(1);
  }, [selectedCircle]);

  useEffect(() => {
    if (!targetPinFromQuery || !properties.length) return;
    if (autoOpenedPin === targetPinFromQuery) return;

    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === targetPinFromQuery.toLowerCase()
    );

    if (matched) {
      setSelectedProperty(matched);
      setIsModalOpen(true);
      setAutoOpenedPin(targetPinFromQuery);
    }
  }, [targetPinFromQuery, properties, autoOpenedPin]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchedProperties]);

  const displayProperties = searchedProperties !== null ? searchedProperties : properties;
  const displayTotalPages = Math.ceil(displayProperties.length / itemsPerPage) || 1;

  const hasValidImage = (prop) => {
    const img = getPropertyImage(prop);
    if (!img) return false;
    return !imageLoadErrors[prop.pin];
  };

  const handleRequestMeeting = async () => {
    if (!selectedProperty) return;
    if (!meetingComment.trim()) {
      toast.error("Please enter a reason or note for the meeting request.");
      return;
    }
    try {
      setRequestingMeeting(true);
      await axios.post("/properties/request-meeting", {
        propertyId: selectedProperty.id,
        userId,
        comment: meetingComment,
      });
      toast.success("Meeting request sent.");
      setShowMeetingModal(false);
      setMeetingComment("");
      setSelectedProperty((prev) =>
        prev
          ? { ...prev, meeting_requested: 1, meeting_resolved: 0, meeting_requested_at: new Date().toISOString() }
          : prev
      );
      fetchProperties(currentPage);
    } catch (err) {
      console.error(err);
      toast.error("Failed to request meeting.");
    } finally {
      setRequestingMeeting(false);
    }
  };

  const handleMarkMeetingResolved = async () => {
    if (!selectedProperty) return;
    try {
      await axios.post("/properties/resolve-meeting", {
        propertyId: selectedProperty.id,
        userId,
      });
      setSelectedProperty((prev) => (prev ? { ...prev, meeting_resolved: 1 } : prev));
      toast.success("Meeting marked as resolved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to mark meeting as resolved.");
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <Sidebar />
      <Toaster position="top-right" />

      <main className="flex-1 p-6 overflow-y-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-red-700">
            Not Resolved Properties
          </h1>
          <p className="text-gray-600 mt-3 text-base italic border-l-4 border-red-400 pl-4 bg-red-50 py-2 rounded-r">
            Hello! The inspector chose "Do Not Reassess" for these properties. Review their reasoning below,
            and request an in-person meeting if further discussion is needed.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h2 className="text-md font-semibold text-gray-800 mb-3">Search Property by PIN</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={searchPin}
                onChange={(e) => setSearchPin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearchByPin();
                  }
                }}
                placeholder="Enter exact PIN..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
              <button
                onClick={handleSearchByPin}
                disabled={searching}
                className="px-5 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60 transition-colors"
              >
                {searching ? "Searching..." : "Search"}
              </button>
              {searchMessage && searchMessage.includes("Found") && (
                <button
                  onClick={handleClearSearch}
                  className="px-5 py-2 rounded-lg bg-gray-500 text-white font-medium hover:bg-gray-600 transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
            {searchMessage && (
              <p className={`mt-3 text-sm ${searchMessage.includes("Found") ? "text-green-600" : "text-red-600"}`}>
                {searchMessage}
              </p>
            )}
          </div>
        </div>


        {!searchedProperties && (
          <div className="flex flex-wrap justify-center gap-4 mb-8 bg-white shadow-md rounded-xl p-4 border border-teal-100">
            <select
              value={selectedCircle}
              onChange={(e) => setSelectedCircle(e.target.value)}
              className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-gray-50 shadow-sm min-w-[180px]"
            >
              <option value="">All Circles</option>
              {circles.map((circle, i) => (
                <option key={i} value={circle}>{circle}</option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayProperties.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {searchedProperties !== null ? "No Property Found" : "No Unresolved Properties"}
              </h3>
              <p className="text-gray-500 max-w-sm mx-auto">
                {searchedProperties !== null
                  ? "No unresolved property found with the entered PIN."
                  : "No unresolved properties found for the selected filters."}
              </p>
              {searchedProperties !== null && (
                <button
                  onClick={handleClearSearch}
                  className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                >
                  View All Properties
                </button>
              )}
            </div>
          ) : (
            displayProperties.map((prop) => {
              const isSelected = normalizePin(prop.pin) && normalizePin(prop.pin) === highlightPin;

              return (
                <div
                  key={prop.id}
                  onClick={() => handlePropertyClick(prop)}
                  data-pin={normalizePin(prop.pin)}
                  className={`bg-white shadow cursor-pointer transition-all hover:shadow-lg hover:border-red-300 hover:scale-[1.02] relative rounded-xl ${isSelected ? "border-2 border-sky-500" : "border border-gray-200"
                    }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}
                  {hasValidImage(prop) ? (
                    <img
                      src={appendCacheBuster(resolveImageUrl(getPropertyImage(prop)), prop._imgBust)}
                      alt="Property"
                      className="w-full h-40 object-cover rounded-t-xl"
                      onError={() => setImageLoadErrors((prev) => ({ ...prev, [prop.pin]: true }))}
                    />
                  ) : (
                    <div className="w-full h-40 bg-gray-200 flex items-center justify-center text-gray-500 rounded-t-xl">
                      No Image
                    </div>
                  )}
                  <div className="p-4 space-y-2">
                    <h3 className="text-lg font-bold text-red-700">PIN: {prop.pin}</h3>
                    <p className="text-sm">
                      <strong>Circle:</strong> {prop.circle}
                    </p>
                    {prop.meeting_requested == 1 && !prop.meeting_resolved && (
                      <div className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-1">
                        <Calendar size={12} /> Meeting Requested
                      </div>
                    )}
                    {prop.inspector_overall_explanation && (
                      <p className="text-xs text-gray-600 line-clamp-2">
                        <strong>Inspector's reason:</strong> {prop.inspector_overall_explanation}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="inline-block text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                        Status: {prop.status}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {displayTotalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              className="px-3 py-1 bg-teal-600 text-white rounded disabled:opacity-50"
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: displayTotalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${currentPage === i + 1 ? "bg-teal-700 text-white" : "bg-gray-200 text-gray-800"
                  }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, displayTotalPages))}
              className="px-3 py-1 bg-teal-600 text-white rounded disabled:opacity-50"
              disabled={currentPage === displayTotalPages}
            >
              Next
            </button>
          </div>
        )}
      </main>

      <AnimatePresence>
        {isModalOpen && selectedProperty && (
          <MOTION.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-start md:items-center overflow-auto z-50 p-4"
            onClick={closeModal}
          >
            <MOTION.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full max-w-4xl rounded-xl shadow-2xl relative h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex-1 overflow-y-auto p-6 pr-14">
                <div className="mb-8">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-gray-500 text-sm">Circle</p>
                      <p className="text-lg font-bold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500 text-sm mb-1">Status</p>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        ✗ Not Reassessed
                      </span>
                    </div>
                  </div>
                </div>

                {selectedProperty.meeting_requested == 1 && !selectedProperty.meeting_resolved && (
                  <div className="mb-6 bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                      <Calendar size={16} /> In-person meeting requested
                      {selectedProperty.meeting_requested_at && (
                        <span className="text-xs font-normal text-purple-600">
                          on {new Date(selectedProperty.meeting_requested_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleMarkMeetingResolved}
                      className="text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                      Mark Resolved
                    </button>
                  </div>
                )}

                {/* {selectedProperty.supervisor_flag_comment && (
                  <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">Supervisor Flag Comment</h3>
                    <p className="text-yellow-700 text-sm whitespace-pre-wrap">
                      {selectedProperty.supervisor_flag_comment}
                    </p>
                  </div>
                )} */}

                {(() => {
                  const agreementItem = (timeline || []).find(
                    (item) => item?.type === "algorithm_response"
                  );
                  if (!agreementItem) return null;
                  const stage = agreementItem.stage || "";
                  let agreementText = "";
                  if (stage.includes("(Agreed)")) agreementText = "(Agreed)";
                  else if (stage.includes("(Disagreed)")) agreementText = "(Disagreed)";
                  else if (stage.includes("(Partially Agreed)")) agreementText = "(Partially Agreed)";
                  const createdAt = agreementItem?.created_at
                    ? new Date(agreementItem.created_at).toLocaleString()
                    : "";
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 shadow-sm">
                      {/* <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="font-semibold text-blue-900">Computer Vision Agreement</h3>
                        {createdAt && <div className="text-xs text-blue-700">{createdAt}</div>}
                      </div> */}
                      <p className="text-blue-800 text-sm whitespace-pre-wrap">
                        Did inspector agree with Computer Vision Assessment?
                        <br />
                        {agreementText}
                      </p>
                    </div>
                  );
                })()}

                {(selectedProperty.inspector_overall_explanation ||
                  selectedProperty.inspector_floor_explanation ||
                  selectedProperty.inspector_landuse_explanation ||
                  selectedProperty.inspector_landarea_explanation ||
                  selectedProperty.inspector_coveredarea_explanation) && (
                    <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">Inspector's Reason for Not Reassessing</h3>
                        {selectedProperty.inspector_explanation_created_at && (
                          <div className="text-sm text-gray-500">
                            {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                      {selectedProperty.inspector_overall_explanation && (
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">
                          {selectedProperty.inspector_overall_explanation}
                        </div>
                      )}
                    </div>
                  )}

                <div className="relative group h-96 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer mb-8">
                  {getPropertyImage(selectedProperty) && !imageLoadErrors[selectedProperty.pin] ? (
                    <>
                      <div
                        className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                        style={{ transform: `rotate(${rotation}deg)` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullImage(true);
                        }}
                      >
                        <img
                          src={appendCacheBuster(resolveImageUrl(getPropertyImage(selectedProperty)), selectedProperty._imgBust)}
                          className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
                          alt="Property"
                          onError={() => setImageLoadErrors((prev) => ({ ...prev, [selectedProperty.pin]: true }))}
                        />
                      </div>
                      <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                        <button
                          onClick={handleRotate}
                          className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                          title="Rotate Image"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                        {rotation !== 0 && (
                          <button
                            onClick={handleSaveRotation}
                            disabled={savingImageRotation}
                            className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                            title="Save Rotation"
                          >
                            {savingImageRotation ? (
                              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-60 bg-gray-200 flex items-center justify-center rounded-lg text-gray-500">
                      No Image
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      PIN: {selectedProperty.pin}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      Circle: {selectedProperty.circle || "N/A"}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      PT-5: {pickFirstValue(selectedProperty, ["refno", "pt5", "pt_5", "pt"]) || "N/A"}
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-3 text-left font-medium">Feature</th>
                          <th className="p-3 text-left font-medium">Admin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-3 font-medium">Floor</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.floor_admin) ? selectedProperty.floor_admin : "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">Land Use</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.landuse_admin) ? selectedProperty.landuse_admin : "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">Land Area</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.landarea_admin)
                              ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards`
                              : "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">Covered Area</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.coveredarea_admin)
                              ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft`
                              : "N/A"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* <div className="flex-shrink-0 border-t border-gray-200 bg-white rounded-b-xl p-6 pt-4 max-h-[30vh] overflow-y-auto">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">Comment Timeline</h3>
                  <div className="text-sm text-gray-500">Chat view</div>
                </div>

                {timelineLoading ? (
                  <div className="text-sm text-gray-600">Loading...</div>
                ) : timelineError ? (
                  <div className="text-sm text-red-600">{timelineError}</div>
                ) : timeline.length === 0 ? (
                  <div className="text-sm text-gray-600">No previous comments found for this property.</div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((item) => {
                      const createdAt = item?.created_at ? new Date(item.created_at).toLocaleString() : "";
                      const role = String(item.user_role || "").toLowerCase();
                      const isSupervisor = item.type === "supervisor" || role === "supervisor";
                      const isInspector = item.type === "inspector_explanation" || role === "inspector";
                      const isAction = item.type === "action";
                      const isDoNotReassessComment = 
                              item.type === "comment" && 
                              item.stage && 
                              item.stage.includes("Inspector Comment") &&
                              Number(selectedProperty?.reassess) === 0;

                      if (isAction) {
                        return (
                          <div key={item.id} className="flex justify-center">
                            <div className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                              {item.stage || item.action}
                              {createdAt ? ` • ${createdAt}` : ""}
                            </div>
                          </div>
                        );
                      }

                      const sideClass = isSupervisor ? "justify-start" : "justify-end";
                      const bubbleClass = isSupervisor
                        ? "bg-red-50 border-red-200 text-red-900"
                        : "bg-blue-50 border-blue-200 text-blue-900";
                      const headerLabel = isSupervisor ? "Supervisor comment" : isInspector ? "Inspector comment" : "Comment";

                      return (
                        <div key={item.id} className={`flex ${sideClass}`}>
                          <div className={`max-w-[85%] border rounded-2xl px-4 py-3 shadow-sm ${bubbleClass}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                              <div className="text-xs font-semibold">
                                {headerLabel}
                                {item.user_name ? ` • ${item.user_name}` : ""}
                                {item.stage ? ` • ${item.stage}` : ""}
                              </div>
                              {createdAt && <div className="text-[11px] opacity-80">{createdAt}</div>}
                            </div>
                            <div className="text-sm whitespace-pre-wrap">
                              {item.text || item.overall || ""}
                              {isDoNotReassessComment && (
                                <span className="ml-1 text-sm">
                                (Property Not Reassessed)
                                </span>
                              )}
                              </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div> */}

              <div className="flex-shrink-0 flex justify-end p-6 pt-4 border-t border-gray-200">
                {/* <div className="flex-shrink-0 flex justify-between items-center p-6 pt-4 border-t border-gray-200"> */}
                {/* <button
                  onClick={() => setShowMeetingModal(true)}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-lg transition-colors"
                >
                  <Calendar size={18} /> Ask Inspector for In-Person Meeting
                </button> */}
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </MOTION.div>
          </MOTION.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFullImage && selectedProperty && getPropertyImage(selectedProperty) && !imageLoadErrors[selectedProperty.pin] && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
            onClick={() => {
              setShowFullImage(false);
              setRotation(0);
            }}
          >
            <div className="absolute top-6 right-6 flex space-x-4 z-10">
              <button
                onClick={handleRotate}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                title="Rotate Image"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => {
                  setShowFullImage(false);
                  setRotation(0);
                }}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
              >
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <MOTION.div
              initial={{ scale: 0.9, rotate: 0 }}
              animate={{ scale: 1, rotate: rotation }}
              exit={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={appendCacheBuster(resolveImageUrl(getPropertyImage(selectedProperty)), selectedProperty._imgBust)}
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                alt="Full Property"
              />
            </MOTION.div>
          </div>
        )}
      </AnimatePresence>

      {showMeetingModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fadeIn">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Request In-Person Meeting</h3>
              <button
                onClick={() => { setShowMeetingModal(false); setMeetingComment(""); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">Let the inspector know why an in-person meeting is needed.</p>
              <textarea
                value={meetingComment}
                onChange={(e) => setMeetingComment(e.target.value)}
                placeholder="Enter reason / notes for the meeting..."
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all resize-none"
              />
              {!meetingComment.trim() && <p className="text-red-500 text-sm mt-2">A reason is required</p>}
            </div>
            <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
              <button
                onClick={() => { setShowMeetingModal(false); setMeetingComment(""); }}
                className="px-5 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestMeeting}
                disabled={!meetingComment.trim() || requestingMeeting}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
              >
                {requestingMeeting ? "Sending..." : (<><Calendar size={18} /> Send Request</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotResolved;